import React, { useEffect, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { compact } from 'lodash'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Typography } from 'transfix-ui/core'
import { AddSpotBiddingRuleModal } from 'apps/spot-quote-control-panel/components/AddSpotBiddingRuleModal'
import { DeleteSpotBiddingRuleModal } from 'apps/spot-quote-control-panel/components/DeleteSpotBiddingRuleModal'
import { Container } from 'components/layout'
import { ISpotBiddingConfig, ISpotBiddingRulePayload } from '../types/spotBiddingConfig'
import {
  getSpotBiddingConfig,
  openAddSpotBiddingRuleModal,
  getGlobalConfig,
} from '../utils/actions/spotBiddingConfig'
import { IStore } from '../utils/reducers/types'
import { BaselineRulesCard } from './BaselineRulesCard'
import { ShipperQuoteType } from './ShipperQuoteType'
import { SpotBiddingRulesTable } from './SpotBiddingRulesTable'

interface IShipperConfigProps {
  displayTitle?: boolean
  displayQuoteType?: boolean
}

interface ISelector {
  shipperConfig: ISpotBiddingConfig
  fetching: boolean
  error?: string
  showAddModal: boolean
  showDeleteModal: boolean
  modalPayload?: ISpotBiddingRulePayload
  modalInProgress: boolean
  modalError?: string
}

export const ShipperConfig = memo<IShipperConfigProps>(
  ({ displayQuoteType = true, displayTitle = true }) => {
    const dispatch = useDispatch()
    const { id } = useParams<{ id: string }>()
    const isGlobalConfig = !id
    const {
      error,
      fetching,
      modalError,
      modalInProgress,
      modalPayload,
      shipperConfig,
      showAddModal,
      showDeleteModal,
    } = useSelector<IStore, ISelector>(({ modals, spotBiddingConfig }) => ({
      shipperConfig: spotBiddingConfig.spotBiddingConfig,
      fetching: spotBiddingConfig.fetching,
      error: spotBiddingConfig.error,
      showAddModal: modals.showAddModal,
      showDeleteModal: modals.showDeleteModal,
      modalPayload: modals.modalPayload,
      modalInProgress: modals.inProgress,
      modalError: modals.error,
    }))

    const { configName, spotBiddingRules } = shipperConfig || {}

    const {
      appointmentTimeMileageDeliveryRules,
      appointmentTimeMileagePickupRules,
      baselineRule,
      carrierWaitTimeRules,
      contractRules,
      countLoadsQuotedRules,
      dateRules,
      distanceToMarketCenterRules,
      dropRules,
      equipmentRules,
      geoDateRules,
      geographicInboundRules,
      geographicOutboundRules,
      globalBaselineRule,
      laneGeographicRules,
      laneTierRules,
      mileageRules,
      multiStopRules,
      onlyBidOnRules,
      proximalHourRules,
      sameDayRule,
      transitDifferenceRules,
      weekendRule,
    } = spotBiddingRules || {}
    useEffect(() => {
      if (isGlobalConfig) {
        dispatch(getGlobalConfig())
      } else {
        dispatch(getSpotBiddingConfig(id))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const openAddModal = () => {
      dispatch(openAddSpotBiddingRuleModal())
    }

    const rules = spotBiddingRules
      ? compact([
          isGlobalConfig ? baselineRule : undefined,
          ...mileageRules,
          ...sameDayRule,
          weekendRule,
          ...dateRules,
          ...proximalHourRules,
          ...appointmentTimeMileagePickupRules,
          ...appointmentTimeMileageDeliveryRules,
          ...equipmentRules,
          ...geographicOutboundRules,
          ...geographicInboundRules,
          ...geoDateRules,
          ...laneGeographicRules,
          ...carrierWaitTimeRules,
          ...countLoadsQuotedRules,
          ...distanceToMarketCenterRules,
          ...laneTierRules,
          ...contractRules,
          ...dropRules,
          ...onlyBidOnRules,
          ...multiStopRules,
          ...transitDifferenceRules,
        ])
      : []

    const ready = !fetching || showAddModal || showDeleteModal
    const title = `${configName} Rules`
    return (
      <>
        <Container>
          {fetching && <Spinner />}
          {error && <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={error} />}
          {ready && (
            <>
              <div className='mb8'>
                {displayTitle && (
                  <Typography variant='h2' data-test='shipper-title'>
                    {title}
                  </Typography>
                )}
                <div className='fr'>
                  <Button color='primary' onClick={openAddModal} data-test='add-rule-button'>
                    Add New Rule
                  </Button>
                </div>
              </div>
              {displayQuoteType && (
                <div className='mb4'>
                  <div className='row'>
                    <div className='col-lg-6'>
                      <ShipperQuoteType spotBiddingConfig={shipperConfig} />
                    </div>
                    <div className='col-lg-6'>
                      <BaselineRulesCard
                        baselineRule={baselineRule}
                        globalBaselineRule={globalBaselineRule}
                      />
                    </div>
                  </div>
                </div>
              )}
              <Card>
                <SpotBiddingRulesTable spotBiddingRules={rules} />
              </Card>
            </>
          )}
          <AddSpotBiddingRuleModal
            errors={modalError || ''}
            showModal={showAddModal}
            spotBiddingConfig={shipperConfig}
            isGlobal={isGlobalConfig}
            saving={modalInProgress}
            rule={modalPayload?.rule}
          />
          <DeleteSpotBiddingRuleModal showModal={showDeleteModal} ruleId={modalPayload?.rule?.id} />
        </Container>
      </>
    )
  }
)
